// DeepSeek API service for figure description parsing and SVG generation
const DEEPSEEK_API_KEY = 'sk-6d1c0e29faa046ba857b2fcc15abd3b3';
const DEEPSEEK_API_URL = 'https://api.deepseek.com/v1/chat/completions';

class DeepSeekFigureParserService {
  constructor() {
    this.apiKey = DEEPSEEK_API_KEY;
    this.baseUrl = DEEPSEEK_API_URL;
  }

  async parseFigureDescription(figureDescription, questionText, onProgress = null) {
    const startTime = Date.now();
    let tokensUsed = 0;
    let estimatedTokens = 1000; // Default estimate for token usage

    try {
      if (onProgress) {
        onProgress({
          stage: 'connecting',
          message: '正在連接 DeepSeek API...',
          tokensUsed: 0,
          timeElapsed: 0
        });
      }

      // Simulate connection time
      await this.delay(500);

      if (onProgress) {
        onProgress({
          stage: 'analyzing',
          message: '分析幾何圖形描述...',
          tokensUsed: 0,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000)
        });
      }

      const prompt = this.createFigureAnalysisPrompt(figureDescription, questionText);
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: this.getFigureAnalysisSystemPrompt()
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.1, // Low temperature for consistent geometric analysis
          max_tokens: 2000,
        }),
      });

      if (onProgress) {
        onProgress({
          stage: 'processing',
          message: '處理幾何圖形數據...',
          tokensUsed: Math.floor(estimatedTokens * 0.5),
          timeElapsed: Math.floor((Date.now() - startTime) / 1000)
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('DeepSeek Figure Parser API Error:', errorText);
        throw new Error(`DeepSeek Figure Parser API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('DeepSeek Figure Parser Response:', data);
      
      tokensUsed = data.usage?.total_tokens || 0;
      
      if (onProgress) {
        onProgress({
          stage: 'completing',
          message: '完成圖形解析...',
          tokensUsed: tokensUsed,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000)
        });
      }

      // Parse the response to extract SVG drawing instructions
      const svgInstructions = this.parseSVGInstructions(data.choices[0].message.content);
      
      if (onProgress) {
        onProgress({
          stage: 'completed',
          message: '圖形解析完成！',
          tokensUsed: tokensUsed,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000),
          cost: this.calculateCost(tokensUsed)
        });
      }

      return {
        success: true,
        svgInstructions,
        tokensUsed,
        timeElapsed: Math.floor((Date.now() - startTime) / 1000),
        cost: this.calculateCost(tokensUsed)
      };

    } catch (error) {
      console.error('DeepSeek Figure Parser Error:', error);
      
      if (onProgress) {
        onProgress({
          stage: 'error',
          message: '圖形解析失敗，使用預設圖形',
          error: error.message,
          timeElapsed: Math.floor((Date.now() - startTime) / 1000)
        });
      }

      return {
        success: false,
        error: error.message,
        svgInstructions: this.getDefaultSVGInstructions(figureDescription),
        timeElapsed: Math.floor((Date.now() - startTime) / 1000)
      };
    }
  }

  getFigureAnalysisSystemPrompt() {
    return `You are an expert geometric figure analyzer and SVG generator with deep understanding of mathematical geometry. Your task is to analyze mathematical figure descriptions and generate precise SVG drawing instructions.

Key responsibilities:
1. Analyze geometric descriptions for circles, triangles, rectangles, polygons, and 3D figures
2. Extract all geometric elements: points, lines, angles, distances, areas, etc.
3. Generate precise SVG drawing instructions in JSON format
4. Handle complex geometric relationships and constraints
5. Understand natural language descriptions and convert them to precise geometric constructions

For each figure type, identify:
- Circle: center, radius, circumference, chords, tangents, arcs, inscribed/circumscribed elements
- Triangle: vertices, sides, angles, altitudes, medians, angle bisectors, area
- Rectangle/Polygon: vertices, sides, diagonals, parallel lines, area
- 3D figures: vertices, edges, faces, projections, angles between planes
- Lines: intersecting lines, parallel lines, perpendicular lines, extensions
- Points: intersection points, midpoints, endpoints, coordinates

Special attention to:
- Line extensions and intersections
- Perpendicular and parallel relationships
- Coordinate geometry with specific points
- Angle measurements and relationships
- Similar triangles and geometric transformations
- Enclosed regions and areas
- Complex geometric constructions with multiple elements

Advanced geometric interpretation:
1. **Coordinate Geometry**: When coordinates are given (e.g., "point A(2,6)"), place them accurately
2. **Line Relationships**: Identify perpendicular (⊥), parallel (∥), and intersecting lines
3. **Extensions**: Lines that extend beyond their endpoints (shown as dashed lines)
4. **Similar Figures**: Triangles or other shapes with proportional relationships
5. **Enclosed Regions**: Areas bounded by lines, curves, or axes
6. **Angle Measurements**: Specific angle values and their relationships
7. **Distance Measurements**: Lengths of sides, radii, or other distances
8. **Geometric Constructions**: Complex figures built from basic elements

Text Analysis Guidelines:
- Look for mathematical symbols: △ (triangle), ∠ (angle), ⊥ (perpendicular), ∥ (parallel)
- Identify point names: A, B, C, D, E, F, etc.
- Recognize line segments: AB, CD, EF, etc.
- Understand coordinate notation: (x,y) or (x, y)
- Parse measurement units: cm, units, degrees
- Detect geometric relationships: "intersects at", "parallel to", "perpendicular to"
- Identify extensions: "extended", "extension of", "produced"

Output format must be valid JSON with the following structure:
{
  "figureType": "circle|triangle|rectangle|polygon|3d|lines|mixed",
  "center": {"x": number, "y": number},
  "radius": number,
  "points": {
    "A": {"x": number, "y": number, "label": "A"},
    "B": {"x": number, "y": number, "label": "B"},
    ...
  },
  "lines": [
    {"from": "A", "to": "B", "color": "string", "width": number, "strokeDasharray": "string"},
    ...
  ],
  "circles": [
    {"center": "O", "radius": number, "color": "string", "width": number},
    ...
  ],
  "angles": [
    {"vertex": "A", "points": ["B", "C"], "measure": number, "label": "string"},
    ...
  ],
  "measurements": [
    {"type": "distance|angle|area", "value": number, "unit": "string"},
    ...
  ],
  "extensions": [
    {"from": "A", "to": "B", "color": "string", "width": number, "strokeDasharray": "5,5"},
    ...
  ]
}

Be precise with coordinates and measurements. Use a 400x400 SVG viewport with origin at top-left.
For dashed lines (extensions), use strokeDasharray: "5,5".
For perpendicular lines, ensure they meet at right angles.
For parallel lines, ensure they maintain equal distance.
For intersecting lines, place intersection points accurately.
For coordinate geometry, place points at their exact coordinates when given.
For similar figures, maintain proportional relationships.
For enclosed regions, clearly define the boundary elements.`;
  }

  createFigureAnalysisPrompt(figureDescription, questionText) {
    return `Analyze the following geometric figure description and generate precise SVG drawing instructions.

Question Context:
${questionText}

Figure Description:
${figureDescription}

Please provide detailed SVG drawing instructions in JSON format that will allow the SVG viewer to accurately render this geometric figure. Include all points, lines, circles, angles, and measurements mentioned in the description.

Focus on:
1. Identifying the main geometric shape (circle, triangle, rectangle, polygon, 3D)
2. Extracting all points and their positions
3. Determining lines, chords, tangents, and other geometric elements
4. Calculating angles and measurements
5. Positioning elements appropriately within a 400x400 viewport

Return only valid JSON without any additional text or explanations.`;
  }

  parseSVGInstructions(responseContent) {
    try {
      // Extract JSON from the response
      const jsonMatch = responseContent.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No JSON found in response');
      }

      const svgData = JSON.parse(jsonMatch[0]);
      
      // Validate and normalize the SVG data
      return this.validateAndNormalizeSVGData(svgData);
    } catch (error) {
      console.error('Error parsing SVG instructions:', error);
      return this.getDefaultSVGInstructions();
    }
  }

  validateAndNormalizeSVGData(svgData) {
    const normalized = {
      figureType: svgData.figureType || 'triangle',
      points: {},
      lines: [],
      circles: [],
      angles: [],
      measurements: []
    };

    // Normalize points
    if (svgData.points) {
      Object.entries(svgData.points).forEach(([key, point]) => {
        if (point && typeof point.x === 'number' && typeof point.y === 'number') {
          normalized.points[key] = {
            x: Math.max(0, Math.min(400, point.x)),
            y: Math.max(0, Math.min(400, point.y)),
            label: point.label || key
          };
        }
      });
    }

    // Normalize lines
    if (svgData.lines && Array.isArray(svgData.lines)) {
      normalized.lines = svgData.lines.filter(line => 
        line.from && line.to && 
        normalized.points[line.from] && 
        normalized.points[line.to]
      ).map(line => ({
        from: line.from,
        to: line.to,
        color: line.color || 'blue',
        width: line.width || 3
      }));
    }

    // Normalize circles
    if (svgData.circles && Array.isArray(svgData.circles)) {
      normalized.circles = svgData.circles.filter(circle => 
        circle.center && circle.radius && 
        normalized.points[circle.center]
      ).map(circle => ({
        center: circle.center,
        radius: Math.max(10, Math.min(200, circle.radius)),
        color: circle.color || 'blue',
        width: circle.width || 3
      }));
    }

    return normalized;
  }

  getDefaultSVGInstructions(figureDescription = '') {
    // Provide sensible defaults based on common geometric terms
    const text = figureDescription.toLowerCase();
    
    if (text.includes('circle') || text.includes('circumference')) {
      return {
        figureType: 'circle',
        points: {
          O: { x: 200, y: 200, label: 'O' },
          A: { x: 320, y: 200, label: 'A' },
          B: { x: 200, y: 80, label: 'B' },
          C: { x: 80, y: 200, label: 'C' },
          D: { x: 200, y: 320, label: 'D' }
        },
        lines: [
          { from: 'A', to: 'B', color: 'blue', width: 3 },
          { from: 'B', to: 'C', color: 'blue', width: 3 },
          { from: 'C', to: 'D', color: 'blue', width: 3 },
          { from: 'D', to: 'A', color: 'blue', width: 3 }
        ],
        circles: [
          { center: 'O', radius: 120, color: 'blue', width: 3 }
        ]
      };
    } else if (text.includes('triangle')) {
      return {
        figureType: 'triangle',
        points: {
          A: { x: 200, y: 50, label: 'A' },
          B: { x: 100, y: 200, label: 'B' },
          C: { x: 300, y: 200, label: 'C' }
        },
        lines: [
          { from: 'A', to: 'B', color: 'gray', width: 3 },
          { from: 'B', to: 'C', color: 'gray', width: 3 },
          { from: 'C', to: 'A', color: 'gray', width: 3 }
        ]
      };
    } else {
      return {
        figureType: 'triangle',
        points: {
          A: { x: 200, y: 50, label: 'A' },
          B: { x: 100, y: 200, label: 'B' },
          C: { x: 300, y: 200, label: 'C' }
        },
        lines: [
          { from: 'A', to: 'B', color: 'gray', width: 3 },
          { from: 'B', to: 'C', color: 'gray', width: 3 },
          { from: 'C', to: 'A', color: 'gray', width: 3 }
        ]
      };
    }
  }

  calculateCost(tokensUsed) {
    // DeepSeek API pricing (approximate)
    const costPer1kTokens = 0.002; // $0.002 per 1k tokens
    return (tokensUsed / 1000) * costPer1kTokens;
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default new DeepSeekFigureParserService(); 